import React from 'react';
import { 
  X, 
  ShieldAlert, 
  CheckCircle2, 
  AlertTriangle, 
  GitBranch 
} from 'lucide-react';
import { ManipulatedVariable } from '../types';
import { CORRELATION_RULES } from '../services/correlationEngine';

interface CorrelationMatrixViewProps {
  isOpen: boolean;
  onClose: () => void;
  mvs: ManipulatedVariable[];
}

export const CorrelationMatrixView: React.FC<CorrelationMatrixViewProps> = ({
  isOpen,
  onClose,
  mvs,
}) => {
  if (!isOpen) return null;

  const activeMvs = mvs.filter((m) => m.status === 'Active' || m.status === 'Waiting for Steady State');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/40 backdrop-blur-xs overflow-y-auto">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden my-4 sm:my-8">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-indigo-600" />
            <h2 className="font-semibold text-slate-900 text-base">
              Correlation Control Matrix
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4 max-h-[82vh] overflow-y-auto">
          {/* Explanation Banner */}
          <div className="p-3.5 bg-indigo-50/60 border border-indigo-100 rounded-xl text-xs text-indigo-900 flex items-start gap-2.5">
            <GitBranch className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
            <p>
              Coupled manipulated variables should not be stepped simultaneously. Stepping coupled variables together induces collinearity and prevents reliable multivariable model matrix decoupling.
            </p>
          </div>

          {/* Currently Active Tests */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
              Currently Active Plant MVs ({activeMvs.length})
            </div>
            {activeMvs.length === 0 ? (
              <div className="text-xs text-slate-400">
                No active step tests currently logged.
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {activeMvs.map((m) => (
                  <span
                    key={m.id}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white text-slate-800 border border-slate-200 text-xs font-mono shadow-xs"
                  >
                    <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                    <span className="font-bold text-sky-700">{m.tag}</span>
                    <span className="text-slate-500 font-sans text-[11px]">({m.controller})</span>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Correlation Rules Cards */}
          <div className="space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Coupling Rules & Constraints
            </div>

            <div className="space-y-2.5">
              {CORRELATION_RULES.map((rule) => {
                const activeInA = activeMvs.filter((m) => rule.tagsA.includes(m.tag));
                const activeInB = activeMvs.filter((m) => rule.tagsB.includes(m.tag));
                const isConflictActive = activeInA.length > 0 && activeInB.length > 0;

                return (
                  <div
                    key={rule.id}
                    className={`p-3.5 rounded-xl border transition-all ${
                      isConflictActive
                        ? 'bg-rose-50 border-rose-200'
                        : 'bg-white border-slate-200 shadow-xs'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-2">
                        {isConflictActive ? (
                          <AlertTriangle className="w-4 h-4 text-rose-600" />
                        ) : (
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        )}
                        <h4 className="font-semibold text-slate-900 text-xs">{rule.name}</h4>
                      </div>

                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${
                          isConflictActive
                            ? 'bg-rose-600 text-white'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {isConflictActive ? 'Conflict Active' : 'Clear'}
                      </span>
                    </div>

                    <p className="text-xs text-slate-600 mb-2.5">{rule.description}</p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-mono">
                      <div className="bg-slate-50 p-2 rounded-lg border border-slate-200">
                        <div className="text-[10px] text-slate-500 uppercase font-semibold mb-1">
                          Group A Variables
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {rule.tagsA.map((t) => (
                            <span
                              key={t}
                              className={`px-1.5 py-0.5 rounded text-[11px] ${
                                activeMvs.some((m) => m.tag === t)
                                  ? 'bg-sky-600 text-white font-bold'
                                  : 'bg-white border border-slate-200 text-slate-700'
                              }`}
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="bg-slate-50 p-2 rounded-lg border border-slate-200">
                        <div className="text-[10px] text-slate-500 uppercase font-semibold mb-1">
                          Coupled Opponents (Do Not Step Together)
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {rule.tagsB.map((t) => (
                            <span
                              key={t}
                              className={`px-1.5 py-0.5 rounded text-[11px] ${
                                activeMvs.some((m) => m.tag === t)
                                  ? 'bg-rose-600 text-white font-bold'
                                  : 'bg-white border border-slate-200 text-slate-700'
                              }`}
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
